import {
  isStationDecoder,
  type DecoderSummary,
} from '@/features/config/hooks/controller/useSearchAllDecoders'

/**
 * SCRUM-1032 / SCRUM-1040 — the station list, started from the decoders.
 *
 * A station is a decoder plus the agronomy attached to it. The decoder half is
 * already entered by the time the wizard reaches this step: every decoder the
 * user marked Irrigation or Non-Irrigation is a station waiting to be named.
 * Retyping those addresses by hand — a thousand of them on a large site — is
 * transcription, and transcription is what the wizard is for.
 *
 * So the step opens with one draft row per station decoder that has no station
 * yet, carrying what the decoder already knows: its address, its name, and
 * whether it irrigates. What it cannot know — area, sub-area, plant, soil,
 * emitter — is left blank for the user, and the save refuses a row still
 * missing them.
 *
 * Drafts, not records: nothing is written until the wizard's Next, and a row
 * that should not exist is deleted like any other.
 */

/** Temp ids count down from here; the grid reads a negative id as "not saved". */
export const STATION_DRAFT_ID_START = -1_000_000

/**
 * Every station gets a priority, and on a list built in one go they are all
 * equal until someone says otherwise. 1 is the value the valve and pump rows
 * already default to, and it is one fewer required field to fill in a thousand
 * times.
 */
const DEFAULT_PRIORITY = 1

/**
 * `stationType` as the grid holds it: a string id, matched against
 * STATION_TYPE_OPTIONS. Non-irrigation is 1 and irrigation is 2 — not 0, which
 * is no option at all and renders as "-".
 */
const NON_IRRIGATION_TYPE = '1'
const IRRIGATION_TYPE = '2'

const normalisePurpose = (purpose: unknown) =>
  String(purpose ?? '')
    .toLowerCase()
    .replace(/[\s_-]+/g, '')

const isNonIrrigation = (decoder: DecoderSummary) =>
  normalisePurpose(decoder?.decoderType) === 'nonirrigation'

/** `address-index` — how a decoder is named on a station row. */
export const decoderAddressOf = (decoder: {
  address?: number | string
  index?: number | string
}) => `${decoder?.address}-${decoder?.index}`

export interface StationDraftRow extends Record<string, any> {
  id: number
  __isNew: true
  decoderAddress: string
  stationName: string
  description: string
  stationType: string
}

interface BuildStationDraftsArgs {
  decoders: DecoderSummary[] | undefined
  /** Stations already on the controller, normalised (they carry decoderAddress). */
  stations: { decoderAddress?: string }[]
  /** Where the temp ids start counting down from. */
  startId?: number
}

/**
 * One draft per station decoder with no station on it, in decoder order.
 *
 * A decoder already driving a station is skipped: that station is the record,
 * and offering a second row against the same address invites a duplicate the
 * controller would reject.
 */
export const buildStationDrafts = ({
  decoders,
  stations,
  startId = STATION_DRAFT_ID_START,
}: BuildStationDraftsArgs): StationDraftRow[] => {
  const taken = new Set(
    stations
      .map((station) => String(station?.decoderAddress ?? ''))
      .filter((address) => address && address !== '-'),
  )

  let nextId = startId

  // One draft per address-index, whatever the controller.
  //
  // On a control hub two sub-controllers can hold the same address and index —
  // 37035-1 exists on twiId 1 and twiId 2 — and the station API treats that
  // address as the thing a station occupies: it accepted a station for each,
  // then refused to update either, saying the decoder was used by another
  // station. Drafting both is what put that pair on the controller.
  const drafted = new Set<string>()

  return (decoders ?? [])
    .filter(isStationDecoder)
    .filter((decoder) => {
      const key = decoderAddressOf(decoder)
      if (taken.has(key) || drafted.has(key)) return false
      drafted.add(key)
      return true
    })
    .map((decoder) => {
      const decoderAddress = decoderAddressOf(decoder)
      // The decoder's own name is the best first guess at the station's, and
      // it is what the server would substitute for a blank one anyway. Falling
      // back to the address keeps the row identifiable rather than nameless.
      const name = String(decoder.name ?? '').trim() || decoderAddress

      const draft: StationDraftRow = {
        id: nextId,
        __isNew: true,
        decoderAddress,
        address: decoder.address,
        index: decoder.index,
        twiId: decoder.twiId,
        decoderId: decoder.id || undefined,
        stationName: name,
        description: name,
        decoderType: decoder.decoderType,
        stationType: isNonIrrigation(decoder)
          ? NON_IRRIGATION_TYPE
          : IRRIGATION_TYPE,
        priority: DEFAULT_PRIORITY,
        // Left for the user; the save marks whichever of these is still blank.
        area: '',
        subArea: '',
        subAreaId: null,
        plantId: null,
        soilId: null,
        emitterId: null,
      }

      nextId -= 1
      return draft
    })
}

/**
 * What the drafts would be built from, as one comparable string.
 *
 * The list is rebuilt when this changes and not otherwise: the user can go back
 * to the decoder step mid-way, mark a decoder non-irrigation or repurpose it as
 * a valve, and return — the station rows have to follow, or the step is showing
 * a list the decoders no longer describe. A plain refetch is not enough to act
 * on, since it returns identical data most of the time.
 */
export const stationDecoderSignature = (
  decoders: DecoderSummary[] | undefined,
): string =>
  (decoders ?? [])
    .filter(isStationDecoder)
    .map((decoder) => `${decoderAddressOf(decoder)}:${decoder.decoderType}`)
    .join('|')

/**
 * The decoder addresses that are still stations.
 *
 * A station whose decoder has since been repurposed — to a master valve, say —
 * is not in this set. It is still a record on the controller, and still drives
 * that decoder, which is exactly the conflict worth showing the user.
 */
export const stationDecoderAddresses = (
  decoders: DecoderSummary[] | undefined,
): Set<string> =>
  new Set(
    (decoders ?? []).filter(isStationDecoder).map((decoder) => decoderAddressOf(decoder)),
  )

/** The station type a decoder's purpose calls for, or null if it is not one. */
export const stationTypeForDecoder = (
  decoder: DecoderSummary | undefined,
): string | null => {
  if (!decoder || !isStationDecoder(decoder)) return null
  return isNonIrrigation(decoder) ? NON_IRRIGATION_TYPE : IRRIGATION_TYPE
}

/** Station-purpose decoders by `address-index`, for checking a saved station. */
export const stationDecodersByAddress = (
  decoders: DecoderSummary[] | undefined,
): Map<string, DecoderSummary> =>
  new Map(
    (decoders ?? [])
      .filter(isStationDecoder)
      .map((decoder) => [decoderAddressOf(decoder), decoder]),
  )

/**
 * Drops the drafts whose decoder now has a station.
 *
 * Run against every fresh copy of the server rows, so a saved draft disappears
 * as its station arrives instead of standing beside it as a duplicate.
 */
export const pruneSavedDrafts = <T extends { decoderAddress?: string }>(
  drafts: T[],
  stations: { decoderAddress?: string }[],
): T[] => {
  const taken = new Set(
    stations
      .map((station) => String(station?.decoderAddress ?? ''))
      .filter((address) => address && address !== '-'),
  )
  return drafts.filter((draft) => !taken.has(String(draft?.decoderAddress ?? '')))
}

/**
 * SCRUM-1032 round 7, item 5 — whether the guided autosave has anything to do.
 *
 * It used to ask only whether drafts were left. Once every decoder had its
 * station there were none, so an edit to a saved station armed nothing and
 * sat unsaved until Next; a reload lost it. Reproduced on D2.
 */
export const hasAutosaveWork = (
  draftCount: number,
  savedRowEditCount: number,
): boolean => draftCount > 0 || savedRowEditCount > 0

/** Staged edits on stations that exist (positive ids), not on drafts. */
export const countSavedRowEdits = (
  pendingEdits: Map<string, unknown> | undefined,
): number => {
  if (!pendingEdits) return 0
  let count = 0
  pendingEdits.forEach((_edit, rowId) => {
    if (Number(rowId) > 0) count += 1
  })
  return count
}

/**
 * The part of a draft's staged edit typed after its station was written.
 *
 * A written draft stays on screen, editable, until the list re-reads and the
 * real station replaces it. What was typed in between is not the write's to
 * own: the write already went, and the draft is skipped from then on as
 * stationed. These fields are what has to follow the row to its new id.
 */
export const editsMadeAfterWrite = (
  current: Record<string, unknown> | undefined,
  written: Record<string, unknown> | undefined,
): Record<string, unknown> => {
  const after: Record<string, unknown> = {}
  // Nothing recorded as written — a draft retired because its decoder already
  // had a station. Its values were never sent, and carrying them would write
  // over a station the user did not touch.
  if (!written) return after
  Object.entries(current ?? {}).forEach(([field, value]) => {
    if (!Object.is(value, written[field])) after[field] = value
  })
  return after
}
